import { clamp01, lerp } from '../../shared/scalar';
import type { Vec2 } from '../../shared/vec';
import { type Spot, polar } from '../followspot';
import { easeInOutCubic, easeOutCubic } from '../motion';

const TAU = Math.PI * 2;
const RISE_BEATS = 0.15;
const SWEEP_BEATS = 1;
const STEP_BEATS = 2;
const FULL_BEATS = 4;
const SETTLE_BEATS = 1;
const WATCH = 0.75;
const CALM_FILL_BEATS = 1;

const turn = (angle: number): number => ((angle % TAU) + TAU) % TAU;

// The exits in the order the followspot visits them, each with how far round from the team it is:
// one way round, whichever has the least to turn.
function visits(from: number, aims: readonly number[]): { index: number; turned: number }[] {
  const way = (sign: 1 | -1): { index: number; turned: number }[] =>
    aims
      .map((aim, index) => ({ index, turned: sign * turn(sign * (aim - from)) }))
      .sort((a, b) => Math.abs(a.turned) - Math.abs(b.turned));
  const span = (visited: { turned: number }[]): number => Math.abs(visited.at(-1)?.turned ?? 0);
  const round = way(1);
  const back = way(-1);
  return span(round) <= span(back) ? round : back;
}

// At sunrise the stage's followspot leaves the team, sweeps to each exit in turn and lands on it,
// leaving a ray there; once all are reached, a ray per exit stays on, in full light for a bar, then
// lower, for as long as they are open. In calm mode the rays do not sweep: they fill from the core.
// One spot per exit, null until the followspot heads for it.
export function exitSpotsOf(
  beats: number,
  core: Vec2,
  team: Vec2,
  exits: readonly Vec2[],
  calm: boolean,
): (Spot | null)[] {
  const targets = exits.map((exit) => polar(core, exit));
  const lastLanding = calm ? CALM_FILL_BEATS : (exits.length - 1) * STEP_BEATS + SWEEP_BEATS;
  const settled = lerp(
    1,
    WATCH,
    easeInOutCubic(clamp01((beats - lastLanding - FULL_BEATS) / SETTLE_BEATS)),
  );
  const light = Math.min(clamp01(beats / RISE_BEATS), settled);
  if (calm) {
    const fill = easeOutCubic(clamp01(beats / CALM_FILL_BEATS));
    return targets.map(({ aim, reach }) => ({ aim, reach: reach * fill, light }));
  }
  const start = polar(core, team);
  const spots: (Spot | null)[] = exits.map(() => null);
  let from = { turned: 0, reach: start.reach };
  visits(
    start.aim,
    targets.map(({ aim }) => aim),
  ).forEach(({ index, turned }, rank) => {
    const to = { turned, reach: targets[index]?.reach ?? 0 };
    const share = easeInOutCubic(clamp01((beats - rank * STEP_BEATS) / SWEEP_BEATS));
    if (beats >= rank * STEP_BEATS) {
      spots[index] = {
        aim: start.aim + lerp(from.turned, to.turned, share),
        reach: lerp(from.reach, to.reach, share),
        light,
      };
    }
    from = to;
  });
  return spots;
}
